import React from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputSwitch } from "primereact/inputswitch";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Fieldset } from "primereact/fieldset";
import agent from "../../../agent";
import { ACTIVE_OPTIONS, CLIENT_OPTIONS } from "./constants";
import CreateDialog from "./CreateDialog";
import EditDialog from "./EditDialog";

const initialFilters = {
    platform: "",
    clientName: "",
    postalType: "",
    active: true,
};

const SlqTariffs = () => {
    const toast = React.useRef(null);

    const [loading, setLoading] = React.useState(false);
    const [saving, setSaving] = React.useState(false);

    const [content, setContent] = React.useState([]);
    const [totalRecords, setTotalRecords] = React.useState(0);

    const [page, setPage] = React.useState(0);
    const [size, setSize] = React.useState(20);

    const [filters, setFilters] = React.useState(initialFilters);
    const [appliedFilters, setAppliedFilters] = React.useState(initialFilters);

    const [createVisible, setCreateVisible] = React.useState(false);
    const [editTariff, setEditTariff] = React.useState(null);

    const [orgs, setOrgs] = React.useState([]);

    React.useEffect(() => {
        agent.Organizations.all()
            .then(res => setOrgs(Array.isArray(res) ? res : (res?.content || [])))
            .catch(() => {});
    }, []);

    const showError = (detail) => toast.current?.show({ severity: "error", summary: "Ошибка", detail, life: 5000 });
    const showSuccess = (detail) => toast.current?.show({ severity: "success", summary: "Успешно", detail, life: 3000 });

    const load = React.useCallback(() => {
        setLoading(true);
        const params = { page, size };
        if (appliedFilters.platform) params.platform = appliedFilters.platform;
        if (appliedFilters.clientName) params.clientName = appliedFilters.clientName;
        if (appliedFilters.postalType) params.postalType = appliedFilters.postalType;
        if (appliedFilters.active !== null && appliedFilters.active !== undefined) params.active = appliedFilters.active;

        agent.SlqTariffs.list(params)
            .then(res => {
                setContent(res?.content || []);
                setTotalRecords(res?.totalElements || 0);
            })
            .catch(err => showError(typeof err === "string" ? err : agent.handleError(err)))
            .finally(() => setLoading(false));
    }, [page, size, appliedFilters]);

    React.useEffect(() => { load(); }, [load]);

    const onApply = () => {
        setPage(0);
        setAppliedFilters({ ...filters });
    };

    const onReset = () => {
        setFilters(initialFilters);
        setAppliedFilters(initialFilters);
        setPage(0);
    };

    const onPage = (e) => {
        setPage(e.page);
        setSize(e.rows);
    };

    const onCreate = (data) => {
        setSaving(true);
        agent.SlqTariffs.create(data)
            .then(() => {
                showSuccess("Тариф создан");
                setCreateVisible(false);
                load();
            })
            .catch(err => showError(typeof err === "string" ? err : agent.handleError(err)))
            .finally(() => setSaving(false));
    };

    const onUpdate = (data) => {
        if (!editTariff) return;
        setSaving(true);
        agent.SlqTariffs.update(editTariff.id, data)
            .then(() => {
                showSuccess("Тариф обновлен");
                setEditTariff(null);
                load();
            })
            .catch(err => showError(typeof err === "string" ? err : agent.handleError(err)))
            .finally(() => setSaving(false));
    };

    const onToggleActive = (row, newActive) => {
        // optimistic update
        setContent(prev => prev.map(t => t.id === row.id ? { ...t, active: newActive } : t));
        agent.SlqTariffs.setActive(row.id, newActive)
            .then(() => showSuccess(`Тариф ${newActive ? "активирован" : "деактивирован"}`))
            .catch(err => {
                // revert
                setContent(prev => prev.map(t => t.id === row.id ? { ...t, active: !newActive } : t));
                showError(typeof err === "string" ? err : agent.handleError(err));
            });
    };

    const onDelete = (row) => {
        confirmDialog({
            message: `Удалить тариф #${row.id}?`,
            header: "Подтверждение",
            icon: "pi pi-exclamation-triangle",
            acceptLabel: "Удалить",
            rejectLabel: "Отмена",
            acceptClassName: "p-button-danger",
            accept: () => {
                agent.SlqTariffs.remove(row.id)
                    .then(() => {
                        showSuccess("Тариф удален");
                        load();
                    })
                    .catch(err => showError(typeof err === "string" ? err : agent.handleError(err)));
            }
        });
    };

    const activeBody = (row) => (
        <InputSwitch checked={!!row.active} onChange={(e) => onToggleActive(row, e.value)} />
    );

    const actionsBody = (row) => (
        <div className="p-d-flex" style={{ gap: 8 }}>
            <Button icon="pi pi-pencil" className="p-button-rounded p-button-text"
                    onClick={() => setEditTariff(row)} />
            <Button icon="pi pi-trash" className="p-button-rounded p-button-text p-button-danger"
                    onClick={() => onDelete(row)} />
        </div>
    );

    const priceBody = (field) => (row) =>
        row[field] != null ? Number(row[field]).toFixed(2) : "";

    // null != 0: наценка не применяется, для PTC цена идёт по себестоимости
    const marginBody = (row) =>
        row.marginPrice == null
            ? <span title="Наценка не применяется: цена по себестоимости">—</span>
            : Number(row.marginPrice).toFixed(2);

    const clientNameBody = (row) => {
        const opt = CLIENT_OPTIONS.find(o => o.value === row.clientName);
        return opt ? opt.name : (row.clientName || "");
    };

    return (
        <div>
            <Toast ref={toast} />
            <ConfirmDialog />

            <div className="p-d-flex p-jc-between p-ai-center">
                <h1>Тарифы SLQ</h1>
                <Button className="p-d-block" label="Создать тариф" icon="pi pi-plus"
                        onClick={() => setCreateVisible(true)} />
            </div>

            <Fieldset legend="Фильтры" toggleable style={{ marginBottom: "10px" }}>
                <div className="p-fluid p-formgrid p-grid">
                    <div className="p-field p-col-6 p-md-3">
                        <label htmlFor="filterPlatform">Платформа</label>
                        <Dropdown inputId="filterPlatform" value={filters.platform}
                                  options={orgs} optionLabel="name" optionValue="name"
                                  onChange={e => setFilters({ ...filters, platform: e.value })}
                                  showClear placeholder="Все" />
                    </div>
                    <div className="p-field p-col-6 p-md-3">
                        <label htmlFor="filterClientName">Клиент</label>
                        <Dropdown inputId="filterClientName" value={filters.clientName}
                                  options={CLIENT_OPTIONS} optionLabel="name" optionValue="value"
                                  onChange={e => setFilters({ ...filters, clientName: e.value })}
                                  showClear placeholder="Все" />
                    </div>
                    <div className="p-field p-col-6 p-md-3">
                        <label htmlFor="filterPostalType">Тип отправления</label>
                        <InputText id="filterPostalType" value={filters.postalType || ""}
                                   onChange={e => setFilters({ ...filters, postalType: e.target.value })} />
                    </div>
                    <div className="p-field p-col-6 p-md-3">
                        <label htmlFor="filterActive">Активность</label>
                        <Dropdown inputId="filterActive" value={filters.active} options={ACTIVE_OPTIONS}
                                  onChange={e => setFilters({ ...filters, active: e.value })} />
                    </div>
                </div>

                <br />
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <div className="p-grid">
                        <div className="p-mr-2">
                            <Button type="submit" label="Отфильтровать" autoFocus onClick={onApply} />
                        </div>
                        <div>
                            <Button className="p-button-warning" type="reset" label="Сбросить"
                                    onClick={onReset} />
                        </div>
                    </div>
                </div>
            </Fieldset>

            <DataTable value={content} dataKey="id"
                       lazy paginator
                       first={page * size}
                       rows={size}
                       totalRecords={totalRecords}
                       onPage={onPage}
                       rowsPerPageOptions={[10, 20, 50]}
                       loading={loading}
                       emptyMessage="Данные не найдены"
                       responsiveLayout="scroll">
                <Column field="id" header="№" style={{ width: 80 }} />
                <Column field="platform" header="Платформа" />
                <Column field="clientName" header="Клиент" body={clientNameBody} />
                <Column field="postalType" header="Тип отправления" />
                <Column field="marginPrice" header="Маржа" body={marginBody} />
                <Column field="insPrice" header="Страховка" body={priceBody("insPrice")} />
                <Column field="active" header="Активен" body={activeBody} style={{ width: 120 }} />
                <Column header="Действия" body={actionsBody} style={{ width: 140 }} />
            </DataTable>

            <CreateDialog visible={createVisible}
                          onHide={() => setCreateVisible(false)}
                          onSubmit={onCreate}
                          loading={saving}
                          orgs={orgs} />

            <EditDialog visible={!!editTariff}
                        tariff={editTariff}
                        onHide={() => setEditTariff(null)}
                        onSubmit={onUpdate}
                        loading={saving} />
        </div>
    );
};

export default SlqTariffs;
